import { allCapabilitiesApp, getAppHref } from "@/client/apps/AppDefinitions";
import type { AudienceState } from "@/client/core/services/audience";
import { audienceStateSignal } from "@/client/core/services/feature-flags.signal";
import { createFeedbackWidget } from "@/client/ui/components/feedback/feedback-widget.component";
import { createPageChangeService } from "@/client/core/services/page-change.service";
import { getUseProductionServer } from "@/client/utils/getUseProductionServer";
import { createEl } from "@/client/utils/createEl";
import type { SignalWithValue } from "@/client/utils/signal";
import { createSvgIcon } from "@/client/ui/design-system/icons";
import {
    getAccountDisplayName,
    getAccountInitials,
    getAccountSecondaryLine,
    logoutWeb,
    webAccountSignal,
    type WebAccount,
} from "@/client/core/services/web-account.signal";
import { createAudienceTag } from "@/client/ui/components/audience-tag.component";
import { createButton } from "@/client/ui/design-system/components/button.component";
import { Theme } from "@/client/ui/design-system/styles/colors.css";
import { hasWebAccount } from "@/client/core/services/web-auth.service";
import { getDisplayMode } from "@/client/utils/getDisplayMode";
import * as styles from "./app-menu.css";

const pageChangeService = createPageChangeService();

export type AppMenuTagVariant =
    | "feature"
    | "v2"
    | "v25"
    | "v3"
    | "v4"
    | "modalMain"
    | "modalProd"
    | "atlantis"
    | "musicAi";

export interface AppMenuTag {
    label: string;
    variant: AppMenuTagVariant;
}

export interface AppMenuConfig {
    title: string;
    description?: string;
    models?: AppMenuTag[];
    /**
     * Tags that change while the page is open, for a capability whose header
     * picker switches model release.
     *
     * Static `models` said "SpliceGen 2.0" no matter which release the picker
     * had moved to, so the details panel contradicted the model actually
     * receiving requests. Takes precedence over `models` when both are given.
     */
    modelsSignal?: SignalWithValue<AppMenuTag[]>;
    isHome?: boolean;
    /**
     * Controls the app itself puts in its details panel, rendered under the
     * tags — Compare, on a capability with more than one model release.
     *
     * Elements rather than labels and handlers: whether one of these belongs
     * on screen at all can depend on state the menu knows nothing about, like
     * how many releases the viewer's audience may run, so the app builds the
     * control and decides when to show it.
     */
    actions?: HTMLElement[];
}

const tagVariantStyle: Record<AppMenuTagVariant, string> = {
    feature: styles.tag,
    v2: styles.tagV2,
    v25: styles.tagV25,
    v3: styles.tagV3,
    v4: styles.tagV4,
    modalMain: styles.tagModalMain,
    modalProd: styles.tagModalProd,
    atlantis: styles.tagAtlantis,
    musicAi: styles.tagMusicAi,
};

// ── Global tools (audience-tiered, same across all apps) ─────────────────────

const kShowFeedbackWidget = false;

interface ToolLink {
    label: string;
    href: string;
    featured?: boolean;
}

// All Capabilities stays for internal and R&D: a useful overview of the
// underlying models. The local-development utilities (example app, design
// system, custom components) are deliberately NOT in the menu — they only
// matter when building apps locally, and remain reachable by URL. The
// audience preview moved to the home header's audience tag.
const kTools: ToolLink[] = [
    { label: "All Capabilities", href: getAppHref(allCapabilitiesApp), featured: true },
];

const commit = import.meta.env.VITE_GIT_COMMIT ?? "Unknown";
const isProductionServer = getUseProductionServer();
const isProductionBuild = import.meta.env.MODE === "production";

const kBuildInfo = [
    `Commit: ${commit}`,
    `Client: ${isProductionBuild ? "Production" : "Local"}`,
    `Server: ${isProductionServer ? "Production" : "Local"}`,
];

const createToolsSection = (label: string, tools: ToolLink[]): HTMLElement[] => {
    const featured = tools.filter((t) => t.featured === true);
    const regular = tools.filter((t) => t.featured !== true);
    return [
        createEl("hr", styles.divider),
        createEl("div", styles.devToolsSection, [
            createEl("p", styles.devToolsSectionLabel, label),
            ...featured.map((t) =>
                createEl("a", styles.devToolFeatured, t.label, (el) => {
                    el.href = t.href;
                    el.addEventListener("click", () => pageChangeService.onPageChange(t.href));
                }),
            ),
            ...regular.map((t) =>
                createEl(
                    "a",
                    styles.devToolLink,
                    [
                        createEl("span", styles.devToolLinkLabel, t.label),
                        createSvgIcon("chevronRight", styles.devToolLinkIcon),
                    ],
                    (el) => {
                        el.href = t.href;
                        el.addEventListener("click", () => pageChangeService.onPageChange(t.href));
                    },
                ),
            ),
        ]),
    ];
};

const createGlobalSections = (state: AudienceState): HTMLElement[] => {
    const sections: HTMLElement[] = [];
    // Effective audiences drive the tools, so previews are faithful.
    if (state.audiences.has("rnd") || state.audiences.has("internal")) {
        sections.push(...createToolsSection("Internal Tools", kTools));
    }
    if (state.audiences.has("rnd")) {
        sections.push(
            createEl(
                "div",
                styles.buildInfoSection,
                kBuildInfo.map((line) => createEl("p", styles.buildInfoLine, line)),
            ),
        );
    }
    return sections;
};

const kEnvVariants = new Set<AppMenuTagVariant>(["modalMain", "modalProd", "atlantis", "musicAi"]);

/** Model tags in two rows: what the model is, then where it runs. */
const createModelTagRows = (models: readonly AppMenuTag[]): HTMLElement[] => {
    const rows: HTMLElement[] = [];
    for (const group of [
        models.filter((m) => !kEnvVariants.has(m.variant)),
        models.filter((m) => kEnvVariants.has(m.variant)),
    ]) {
        if (group.length > 0) {
            rows.push(
                createEl(
                    "div",
                    styles.tagList,
                    group.map((m) => createEl("span", tagVariantStyle[m.variant], m.label)),
                ),
            );
        }
    }
    return rows;
};

export const createAppMenu = (config: AppMenuConfig): HTMLElement => {
    let isOpen = false;

    // A signal wins over a static list: a capability whose header picker
    // switches release has to re-tag itself, or the panel names a model that
    // is no longer the one receiving requests.
    const modelTagRows: HTMLElement[] = [];
    if (config.modelsSignal !== undefined) {
        const tagsEl = createEl("div", styles.tagRows, []);
        config.modelsSignal.listenAndInit((models) => {
            tagsEl.replaceChildren(...createModelTagRows(models));
        });
        modelTagRows.push(tagsEl);
    } else if (config.models !== undefined && config.models.length > 0) {
        modelTagRows.push(createEl("div", styles.tagRows, createModelTagRows(config.models)));
    }

    // Web shows the conventional hamburger; the embedded panel shows a tools
    // glyph instead — there the menu is app details, not site navigation.
    // Both render and display-mode CSS picks one.
    const hamburgerIcon = createSvgIcon("hamburger", styles.triggerIconWeb);
    const infoIcon = createSvgIcon("infoCircleSolid", styles.triggerIconCompact);
    const closedIcons = [hamburgerIcon, infoIcon];
    const closeIcon = createSvgIcon("x", styles.triggerIcon);
    const devToolsHost = createEl("div", "", []);

    // On the website's home page the menu is also the account menu: the
    // trigger is the signed-in person's avatar and stays so while the menu is
    // open, and the panel leads with who is signed in, their access tier (the
    // chip that used to sit beside the title) and a way out. Nothing of this
    // exists in the plugin surfaces, where the host owns the account, and it
    // waits for the web login to say who it is; until then the plain menu.
    const hasAccountMenu = config.isHome === true && getDisplayMode() === "web" && hasWebAccount();
    let account: WebAccount | undefined;
    const avatarEl = createEl("span", styles.avatar, []);
    const renderAvatar = (): void => {
        if (account === undefined) {
            return;
        }
        if (account.picture !== undefined && account.picture !== "") {
            const img = createEl("img", styles.avatarImage, [], (el) => {
                el.src = account?.picture ?? "";
                el.alt = "";
                el.referrerPolicy = "no-referrer";
            });
            avatarEl.replaceChildren(img);
        } else {
            avatarEl.replaceChildren(
                createEl("span", styles.avatarInitials, getAccountInitials(account)),
            );
        }
    };
    const accountNameEl = createEl("p", styles.accountName, "");
    const accountEmailEl = createEl("p", styles.accountEmail, "");
    const accountSection = createEl("div", styles.accountSection, [
        accountNameEl,
        accountEmailEl,
        createEl("div", styles.accountTierRow, [createAudienceTag()]),
    ]);
    accountSection.style.display = "none";
    // Last in the panel, under whatever tools and build info the tier gets:
    // the way out sits at the foot of the menu, not beside the name.
    const logoutSection = createEl("div", styles.accountActions, [
        createButton({
            label: "Log out",
            theme: Theme.Dark,
            variant: "default",
            onClick: () => {
                void logoutWeb();
            },
        }),
    ]);
    logoutSection.style.display = "none";

    const homeLinkRow = createEl("div", styles.homeLinkRow, [
        createEl(
            "a",
            styles.homeLink,
            [
                createSvgIcon("spliceLogo", styles.homeLinkIcon),
                createEl("span", styles.homeLinkText, "Backstage"),
            ],
            (el) => {
                el.href = "/";
                el.addEventListener("click", () => pageChangeService.onPageChange("/"));
            },
        ),
    ]);
    const aboutSection = createEl("div", styles.section, [
        ...(config.isHome !== true ? [createEl("p", styles.appTitle, config.title)] : []),
        ...(config.description !== undefined
            ? [createEl("p", styles.appDescription, config.description)]
            : []),
        ...modelTagRows,
        ...(config.actions !== undefined && config.actions.length > 0
            ? [createEl("div", styles.actionRow, config.actions)]
            : []),
    ]);

    // The account menu is about the person, so it opens on them rather than
    // on a link home (it is home) or the site's description.
    const panelEl = createEl("div", styles.panel, [
        ...(hasAccountMenu ? [] : [homeLinkRow]),
        createEl("div", styles.panelBody, [
            ...(hasAccountMenu ? [accountSection] : [aboutSection]),
            // Temporarily hidden while the feedback UX is reworked
            // (2026-07-30). The widget and the /telemetry pipeline stay
            // intact — flip kShowFeedbackWidget to bring it back.
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- deliberate temporary kill switch
            ...(kShowFeedbackWidget
                ? [createEl("hr", styles.divider), createFeedbackWidget()]
                : []),
            devToolsHost,
            ...(hasAccountMenu ? [createEl("hr", styles.divider), logoutSection] : []),
        ]),
    ]);

    // Audience-tiered chrome: R&D gets the dev toolbox, build info, and the
    // audience preview toggle; internal gets All Capabilities only (dev
    // builds default to all audiences, so local development keeps it all).
    audienceStateSignal.listenAndInit((state) => {
        devToolsHost.replaceChildren(...createGlobalSections(state));
    });

    const isAvatarTrigger = (): boolean => hasAccountMenu && account !== undefined;

    const close = () => {
        isOpen = false;
        panelEl.classList.remove(styles.panelOpen);
        if (!isAvatarTrigger()) {
            triggerEl.replaceChildren(...closedIcons);
        }
        triggerEl.setAttribute("aria-label", isAvatarTrigger() ? "Open account menu" : "Open menu");
    };

    const open = () => {
        isOpen = true;
        panelEl.classList.add(styles.panelOpen);
        if (!isAvatarTrigger()) {
            triggerEl.replaceChildren(closeIcon);
        }
        triggerEl.setAttribute(
            "aria-label",
            isAvatarTrigger() ? "Close account menu" : "Close menu",
        );
    };

    const triggerEl = createEl("button", styles.trigger, closedIcons, (el) => {
        el.type = "button";
        el.setAttribute("aria-label", "Open menu");
        el.addEventListener("click", (e) => {
            e.stopPropagation();
            if (isOpen) {
                close();
            } else {
                open();
            }
        });
    });

    // Subscribed after the trigger exists: the signal replays its current
    // value synchronously, and that value swaps the trigger's contents.
    if (hasAccountMenu) {
        webAccountSignal.listenAndInit((next) => {
            account = next;
            if (account === undefined) {
                accountSection.style.display = "none";
                logoutSection.style.display = "none";
                triggerEl.classList.remove(styles.triggerAvatar);
                triggerEl.replaceChildren(...(isOpen ? [closeIcon] : closedIcons));
                return;
            }
            renderAvatar();
            accountNameEl.textContent = getAccountDisplayName(account);
            const secondary = getAccountSecondaryLine(account);
            accountEmailEl.textContent = secondary ?? "";
            accountEmailEl.style.display = secondary === undefined ? "none" : "";
            accountSection.style.display = "";
            logoutSection.style.display = "";
            triggerEl.classList.add(styles.triggerAvatar);
            triggerEl.replaceChildren(avatarEl);
            triggerEl.setAttribute(
                "aria-label",
                isOpen ? "Close account menu" : "Open account menu",
            );
        });
    }

    // Panel before trigger so trigger renders on top
    const rootEl = createEl("div", styles.root, [panelEl, triggerEl]);

    document.addEventListener("click", (e) => {
        if (isOpen && !rootEl.contains(e.target as Node)) {
            close();
        }
    });

    document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && isOpen) {
            close();
        }
    });

    return rootEl;
};
